import pluralize from 'pluralize'
import { type ChangeEvent, type ReactNode, useCallback, useState } from 'react'
import toast from 'react-hot-toast'

import { useFvNavigate } from '@fv/client-components'
import { submitForm } from '@fv/client-core'
import type { Quote } from '@fv/client-types'
import { fvDate, fvDateUtc } from '@fv/dates'
import {
  type CarrierBookSettings,
  type Country,
  isAlternateBrand,
  type RetenderReason,
} from '@fv/models'
import {
  type EquipmentAccessorial,
  isFlatbedEquipment,
} from '@fv/models/equipment'

import { refrigeratedEquipmentTypes, supportMessage } from '@/constants'
import {
  optionalCustomsBrokers,
  optionalFormBillTo,
} from '@/features/booking/bookingPayloadHelpers'
import {
  buildCustomerRoutedEquipmentFromFormItems,
  buildCustomerRoutedLoadPayload,
} from '@/features/customer-routed/customerRoutedMappers'
import {
  buildAwardLoadsPayloadCommon,
  buildTruckloadAwardLoadEntry,
} from '@/features/shipment-form/awardLoadMappers'
import { useQueryParam } from '@/hooks/settings'
import { routes } from '@/routes'
import type {
  AwardLoadsMutationArgs,
  ClientCreateCustomerRoutedLoadInput,
} from '@/types'
import { buildSharesDto } from '@/utils/shares'
import {
  getCustomsBrokerLocationContext,
  getFirstSequenceOfType,
  hasDeclaredValue,
  hasFlatbedDetails,
} from '@/utils/shipmentFuncs'

import { usePermissions } from '../auth'
import { billToMapper } from '../billTo/billToFuncs'
import { useCreateCustomerRoutedLoad } from '../book/useCreateCustomerRouted'
import { hasHazardousItems } from '../commodities/commodityUtils'
import {
  createDraftLoadId,
  isDraftLoadId,
} from '../customer-routed/draftLoadId'
import { orderNumberFromRefNums } from '../customer-routed/hooks'
import { customsBrokerMapper } from '../customsBroker/brokerFuncs'
import { emergencyContactMapper } from '../emergencyContact/contactFuncs'
import { isCheapestQuote } from '../quote-reason/hooks'
import { useAccountSettings } from '../settings/account-settings/hooks'
import { buildCommodities } from './helpers'
import {
  bookingFormContext,
  useBookingPageNavigation,
  useLocations,
} from './hooks'
import { useAwardLoads } from './mutations'
import {
  type BookFormLoad,
  type BookingMode,
  billToKey,
  composeLocationKey,
  customerRoutedCarrierKey,
  defaultCustomerRoutedFormData,
  emergencyContactKey,
  type FormSection,
  type FormSectionKey,
  type PendingLoadToBook,
} from './types'
import { useFormSections } from './useFormSections'

const defaultCustomerRoutedSettings: CarrierBookSettings = {
  allowTLPostalCodeChange: true,
  requireCustomsBrokerValue: false,
  requireOriginEmail: false,
  requiresDeclaredValue: false,
  requiresFlatbedDetails: false,
  requiresFullBOLDetails: false,
}

type Props = {
  children: ReactNode
  mode?: 'quoted' | 'customerRouted'
  loadsToBook: PendingLoadToBook[]
  selectedQuotes?: Quote[]
  settings?: CarrierBookSettings
  allQuotes?: {
    [key: string]: Quote[]
  }
}

export const BookingFormProvider = ({
  children,
  mode: bookingMode = 'quoted',
  loadsToBook,
  selectedQuotes = [],
  allQuotes = {},
  settings = defaultCustomerRoutedSettings,
}: Props) => {
  const isCustomerRouted = bookingMode === 'customerRouted'
  const { canBook } = usePermissions()
  const { leave } = useBookingPageNavigation()
  const navigate = useFvNavigate()
  const accountSettings = useAccountSettings()
  const awardLoads = useAwardLoads()
  const createCustomerRouted = useCreateCustomerRoutedLoad()
  const mode = useQueryParam<BookingMode>('mode')
  const isRetender = mode === 'retender'
  const pickup = loadsToBook[0].pickup
  const retenderReason = useQueryParam<RetenderReason>('reason')

  const { locations, setLocations } = useLocations(loadsToBook)
  const [loads, setLoads] = useState(() => buildFormLoads(loadsToBook))
  const isHazardous = hasHazardousItems(loads)
  const { location: customBrokerLoc } =
    getCustomsBrokerLocationContext(locations)
  const requireQuoteReason =
    !isCustomerRouted && accountSettings.booking?.requireReasonForQuoteChoice
      ? loadsToBook.some(
          l =>
            l.quoteId &&
            !isCheapestQuote(
              allQuotes[l.loadId] ?? [],
              selectedQuotes.find(q => q._id === l.quoteId),
            ),
        )
      : false

  const {
    activateSection,
    activeSection,
    formSections,
    goToNextSection,
    setFormSections,
    setPickupOnlySections,
  } = useFormSections(
    loadsToBook,
    isHazardous,
    !!customBrokerLoc,
    isCustomerRouted,
  )

  const [customerRoutedData, setCustomerRoutedData] = useState(
    defaultCustomerRoutedFormData,
  )

  const [billTo, setBillTo] = useState(() =>
    billToMapper.dtoToForm(loadsToBook[0].billTo),
  )
  const [customsBrokers, setCustomsBrokers] = useState(() =>
    (loadsToBook[0].customsBroker?.length
      ? loadsToBook[0].customsBroker
      : [{ country: 'us' as Country, companyName: '', phone: '' }]
    ).map(customsBrokerMapper.dtoToForm),
  )
  const [emergencyContact, setEmergencyContact] = useState(() =>
    emergencyContactMapper.dtoToForm(loadsToBook[0].emergencyContact),
  )
  const [tags, setTags] = useState(
    () => loadsToBook[0].tags?.map(t => t.name) ?? [],
  )
  const [glCode, setGLCode] = useState(() => loadsToBook[0].glCode ?? '')
  const [brandId, setBrandId] = useState<string | undefined>(() =>
    isAlternateBrand(loadsToBook[0].brand)
      ? loadsToBook[0].brand._id
      : undefined,
  )
  const [bolNumber, setBolNumber] = useState(
    loadsToBook[0].bol?.bolNumber ?? '',
  )
  const [shareEmails, setShareEmails] = useState('')

  // Allows entering commodity and billTo info later
  const [isPickupOnly, setPickupOnly] = useState(false)
  const isPending = loadsToBook[0].status === 'pending'
  const hasPickupOnlyPrompt =
    !isCustomerRouted &&
    mode !== 'finishBOL' &&
    !isRetender &&
    !settings.requiresFullBOLDetails &&
    (isPending || loadsToBook[0].bol?.status === 'not-requested')

  function onPickupOnlyChange(e: ChangeEvent<HTMLInputElement>) {
    const pickupOnly = e.currentTarget.checked
    setPickupOnly(e.currentTarget.checked)
    setPickupOnlySections(pickupOnly)
  }

  const setFormRef = useCallback(
    (sectionKey: FormSectionKey, ref: FormSection['ref']) => {
      setFormSections(prev =>
        prev.map(s => {
          if (s.key !== sectionKey) return s
          return { ...s, ref }
        }),
      )
    },
    [setFormSections],
  )

  function buildCustomerRoutedDTO(): ClientCreateCustomerRoutedLoadInput {
    const orderNumber = orderNumberFromRefNums(loadsToBook[0]?.refNums)
    const customsBrokersDto = optionalCustomsBrokers(customsBrokers, locations)
    const emergencyContactDto = isHazardous
      ? emergencyContactMapper.formToDTO(emergencyContact)
      : undefined

    return buildCustomerRoutedLoadPayload({
      billTo: billToMapper.forApiPayload(billToMapper.formToDTO(billTo)),
      bolNumber,
      brandId,
      carrierId: customerRoutedData.carrierId ?? '',
      ...(customerRoutedData.contactEmail && {
        spotQuoteContactEmail: customerRoutedData.contactEmail,
      }),
      createBOL: !isPickupOnly,
      ...(customsBrokersDto && { customsBroker: customsBrokersDto }),
      ...(emergencyContactDto && { emergencyContact: emergencyContactDto }),
      equipment: loads.map(load =>
        buildCustomerRoutedEquipmentFromFormItems({
          accessorials: load.equipment.accessorials ?? [],
          alternateTypes: load.equipment.alternateTypes ?? [],
          declaredValue: load.equipment.declaredValue,
          declaredValueCurrency: load.equipment.declaredValueCurrency ?? 'usd',
          description: load.equipment.description,
          isHazardous: hasHazardousItems([load]),
          items: load.items,
          loadId: isDraftLoadId(load.loadId) ? undefined : load.loadId,
          mode: 'truckload',
          type: load.type,
          weight: load.weight,
          weightUOM: load.weightUOM,
        }),
      ),
      glCode,
      isFreightCollect: true,
      isLiveLoad: true,
      locations,
      ...(orderNumber && { orderNumber }),
      ...(loadsToBook[0]?.templateId && {
        templateId: loadsToBook[0].templateId,
      }),
      schedulePickup: true,
      shares: buildSharesDto(shareEmails, locations),
      tags,
      workflow: 'truckload',
    })
  }

  function validateFormSections() {
    const validSectionKeys: string[] = []
    formSections.forEach(s => s.ref?.current?.checkValidity())
    const invalidSections = formSections.filter(s => !s.isValid)
    let invalidSection = invalidSections.shift()
    let isValidShipment = true

    while (invalidSection) {
      if (!invalidSection.ref?.current) {
        isValidShipment = false
        break
      }

      const formRef = invalidSection.ref.current
      const isValid = formRef.checkValidity()

      if (isValid) {
        validSectionKeys.push(invalidSection.key)
        invalidSection = invalidSections.shift()
      } else {
        isValidShipment = false
        submitForm(formRef)
        break
      }
    }

    if (validSectionKeys.length > 0) {
      setFormSections(prev =>
        prev.map(s => {
          if (!validSectionKeys.includes(s.key)) return s
          return { ...s, isValid: true }
        }),
      )
    }

    return isValidShipment
  }

  function bookShipment(): void {
    if (!canBook) {
      toast.error('You do not have the required permissions.')
      return
    }

    if (isCustomerRouted) {
      if (createCustomerRouted.isPending || createCustomerRouted.isSuccess)
        return

      if (!customerRoutedData.carrierId) {
        toast.error('Please select a carrier.')
        activateSection(customerRoutedCarrierKey)
        return
      }

      if (!validateFormSections()) return

      if (
        accountSettings?.glCode?.enabled &&
        accountSettings?.glCode?.required &&
        !glCode
      ) {
        toast.error('GL Code is required')
        return
      }

      createCustomerRouted.mutate(buildCustomerRoutedDTO(), {
        onSuccess: shipments => {
          navigate(routes.details(shipments[0].loadId))
        },
      })
      return
    }

    if (awardLoads.isPending || awardLoads.isSuccess) return

    if (isRetender) {
      if (
        isFlatbedEquipment(loads[0].type) &&
        settings.requiresFlatbedDetails &&
        !hasFlatbedDetails({ locations })
      ) {
        const ix = locations.findIndex(l => !l.flatbedHandlingType)
        activateSection(`LOCATION_${ix}`)
        toast.error('Flatbed details are required')
        return
      }
      if (settings.requiresDeclaredValue && !hasDeclaredValue(loads[0])) {
        activateSection(`LOAD_${loadsToBook[0].loadId}`)
        toast.error('Declared value is required')
        return
      }
    }

    if (!validateFormSections()) return

    if (isPending) {
      const pickupDate = fvDate(locations[0].stopDate)
      const changedPickupDate = selectedQuotes.find(q => {
        if (!q.pickupDate) return false
        const quotedPickupDate = fvDateUtc(q.pickupDate)
        return !quotedPickupDate.isSame(pickupDate, 'day')
      })?.pickupDate

      if (changedPickupDate) {
        const prev = fvDateUtc(changedPickupDate).format('MMM D')
        const next = fvDate(pickupDate).format('MMM D')
        const prompt = `You have changed the pick up date from ${prev} to ${next} which may result in a different rate than what was originally quoted. Are you sure you want to continue booking?`

        if (!window.confirm(prompt)) {
          const originKey = composeLocationKey(0)

          setFormSections(prev =>
            prev.map(s => {
              if (s.key !== originKey) return s
              return { ...s, isValid: false }
            }),
          )

          return
        }
      }
    }

    if (
      accountSettings?.glCode?.enabled &&
      accountSettings?.glCode?.required &&
      !glCode
    ) {
      toast.error('GL Code is required')
      return
    }

    const isEdit = isRetender || (!isPending && pickup?.status !== 'error')
    const billToDTO = !isPickupOnly ? optionalFormBillTo(billTo) : undefined
    const customsBrokersDto = !isPickupOnly
      ? optionalCustomsBrokers(customsBrokers, locations)
      : undefined
    const emergencyContactDto =
      !isPickupOnly && isHazardous
        ? emergencyContactMapper.formToDTO(emergencyContact)
        : undefined

    const payload = buildAwardLoadsPayloadCommon({
      createBOL: !isPickupOnly,
      loads: loads.map(load =>
        buildTruckloadAwardLoadEntry(load, isPickupOnly),
      ),
      locations,
      shares: buildSharesDto(shareEmails, locations),
      glCode,
      brandId,
      ...(billToDTO && { billTo: billToDTO }),
      ...(customsBrokersDto && { customsBroker: customsBrokersDto }),
      ...(emergencyContactDto && { emergencyContact: emergencyContactDto }),
    })

    const loadsDTO: AwardLoadsMutationArgs = isEdit
      ? {
          isEdit: true,
          ...payload,
          ...(isRetender && {
            retender: true,
            ...(retenderReason && { retenderReason }),
          }),
        }
      : {
          ...payload,
          schedulePickup: true,
          bolNumber,
          tags,
        }

    awardLoads.mutate(loadsDTO, {
      onSuccess: ({ loads }) => leave(loads),
      onError: error => {
        let errorMessage = `Unable to award ${pluralize(
          'load',
          loads.length,
        )}, ${supportMessage}`

        const { dataPath, message } = (error as any)?.errors?.[0] ?? {}
        const path = dataPath?.split('.').filter(Boolean)
        const formSection = path?.[0]

        if (path?.length && message) {
          let sectionKey: FormSectionKey | null = null
          errorMessage = `Error at ${path.join(' ')}: ${message}`

          if (formSection === 'billTo') sectionKey = billToKey
          if (formSection === 'emergencyContact') {
            sectionKey = emergencyContactKey
          }

          if (sectionKey) {
            setFormSections(prev =>
              prev.map(s => {
                if (s.key !== sectionKey) return s
                return { ...s, isValid: false }
              }),
            )
          }
        }

        awardLoads.reset()
        toast.error(errorMessage)
      },
    })
  }

  return (
    <bookingFormContext.Provider
      value={{
        activateSection,
        activeSection,
        billTo,
        bolNumber,
        setBolNumber,
        bookShipment,
        customsBrokers,
        customerRoutedData,
        emergencyContact,
        formSections,
        goToNextSection,
        hasPickupOnlyPrompt,
        isBooking:
          awardLoads.isPending ||
          awardLoads.isSuccess ||
          createCustomerRouted.isPending ||
          createCustomerRouted.isSuccess,
        isCustomerRouted,
        isPickupOnly,
        isRetender,
        loads,
        locations,
        onPickupOnlyChange,
        pickup,
        quoteSelectionReason: loadsToBook[0].quoteSelectionReason,
        requireQuoteReason,
        selectedQuotes,
        setBillTo,
        setCustomerRoutedData,
        setCustomsBrokers,
        setEmergencyContact,
        setFormRef,
        setFormSections,
        setLoads,
        setLocations,
        settings,
        tags,
        setTags,
        glCode,
        setGLCode,
        brandId,
        setBrandId,
        shareEmails,
        setShareEmails,
      }}
    >
      {children}
    </bookingFormContext.Provider>
  )
}

function buildFormLoads(loads: PendingLoadToBook[]): BookFormLoad[] {
  const firstDropSequence = getFirstSequenceOfType(
    loads[0].locations,
    'delivery',
  )

  return loads.map((load, index) => {
    const { equipment, loadId, isMultiTruck, quoteId, status, quoteRequestId } =
      load
    const formLoadId = loadId ?? createDraftLoadId(index)
    const equipmentAccessorials = equipment.accessorials?.slice() ?? []
    const tempSettings = equipmentAccessorials.find(a => a.key === 'temp')
    const isTempControlRequired = refrigeratedEquipmentTypes.includes(
      equipment.type,
    )

    if (isTempControlRequired && !tempSettings) {
      equipmentAccessorials.push({ key: 'temp' } as EquipmentAccessorial)
    }

    return {
      equipment: {
        ...equipment,
        accessorials: equipmentAccessorials,
      },
      items: buildCommodities(load, firstDropSequence),
      isMultiTruck,
      isFreightCollect: load.isFreightCollect,
      loadId: formLoadId,
      quoteId: quoteId ?? '',
      quoteRequestId,
      status,
      type: equipment.type,
      weight: equipment.weight,
      weightUOM: equipment.weightUOM,
    }
  })
}
